import { setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import { useEffect, useState } from "react";
import QRCode from "qrcode";

import BouncingDotsLoader from "./Loader/BouncingDotsLoader";
import { useTokenCache } from "../contexts/TokenCache";
import { getShortAppName } from "../nhd-config";

export default function TotpEnroller({ user }) {
  // TODO: probably need a completion event for use other than in OOBE
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpQr, setTotpQr] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { refreshTokens } = useTokenCache();

  useEffect(() => {
    if (totpSecret)
      return;

    (async () => {
      const setupDetails = await setUpTOTP();
      setTotpSecret(setupDetails.sharedSecret);

      // The fallback case here is to support existing login sessions created with older versions of Amplify JS.
      // This can be simplified to only check signInDetails after the frontend using Amplify JS v6 is deployed to production.
      const accountName = user.signInDetails?.loginId
        ?? user.userData?.UserAttributes?.find(a => a.Name === "email")?.Value
        ?? "";

      const totpUri = setupDetails.getSetupUri(getShortAppName(), accountName).toString();
      setTotpQr(await QRCode.toDataURL(totpUri, { margin: 0 }));
    })();
  }, [totpSecret, user]);

  if (!totpSecret)
    return <BouncingDotsLoader />;

  async function verifyCode(e) {
    setLoading(true);
    setError("");
    e?.preventDefault();

    try {
      await verifyTOTPSetup({ code });

      await updateMFAPreference({ email: "DISABLED", totp: "PREFERRED" });

      await refreshTokens(true);
    }
    catch (ex) {
      setError(ex.message);
    }
    finally {
      setLoading(false);
    }
  }

  return (
    <>
      <p>To access the National Hazard Directory, you will need to set up two-factor authentication using a mobile device.</p>
      
      <p>1. Install an authenticator app.</p>
      <p>2. Add a new account to your authenticator app by scanning the code below.</p>
      
      <div style={{ placeSelf: "center", marginBottom: "1em" }}>
        <img src={totpQr} alt="Barcode (scan to set up authenticator app)" />
      </div>

      <details style={{ marginBottom: "1em" }}>
        <summary style={{ cursor: "pointer" }}>Problems scanning?</summary>
        <p>Instead of scanning, you can type the secret key below into your authenticator app's "Manual entry" or equivalent field. Your app will then generate a 6-digit code you can use.</p>
        <p>Secret Key<code style={{ display:"block", wordBreak: "break-all" }}>{totpSecret}</code></p>
      </details>
      
      <p>3. To verify setup, enter a 6-digit code from your app below. You'll need to do this each time you sign in.</p>
      
      <form onSubmit={verifyCode}>
        <p>
          <label className="amplify-label">
            Enter code
            <input className="amplify-input" placeholder="######" type="text" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value)} />
          </label>
        </p>

        {error ? <p className="amplify-flex amplify-alert amplify-alert--error">{error}</p> : ""}
        {loading ? <BouncingDotsLoader /> :
          <p><button className="amplify-button amplify-button--primary amplify-button--fullwidth" type="submit">Verify and continue</button></p>}
      </form>
    </>
  );
}
